import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import api from '../lib/api';
import { toast } from 'sonner';
import { LOK_SABHA } from '../config/deploymentScope';

const DashboardContext = createContext(null);

const CACHE_DURATION = 30 * 1000; // 30 seconds
// v4: payloads are now scoped to one parliamentary seat. A cached v3 entry
// holds state-wide counts, so it must not be read back under the new key.
const CACHE_KEY = 'dashboardCache_v4';

// Every stat endpoint below accepts `lok_sabha` and narrows to that seat's
// assembly segments. Sent once here so the Overview page's alert, report,
// grievance, sentiment and category tiles all agree on scope.
const SCOPE_PARAMS = LOK_SABHA ? { lok_sabha: LOK_SABHA } : {};

export const DashboardProvider = ({ children }) => {
  const [dashboardData, setDashboardData] = useState(null);
  const [lastFetchTime, setLastFetchTime] = useState(null);
  const [loading, setLoading] = useState(false);

  // Restore from localStorage on mount
  useEffect(() => {
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      if (!cached) return;
      const parsed = JSON.parse(cached);
      if (parsed?.data && parsed?.ts) {
        setDashboardData(parsed.data);
        setLastFetchTime(parsed.ts);
      }
    } catch (error) {
      console.error('Failed to read dashboard cache:', error);
    }
  }, []);

  const fetchDashboardData = useCallback(async (forceRefresh = false) => {
    // Return cached data if available and not expired
    if (!forceRefresh && dashboardData && lastFetchTime) {
      const timeSinceLastFetch = Date.now() - lastFetchTime;
      if (timeSinceLastFetch < CACHE_DURATION) {
        return dashboardData;
      }
    }

    // Prevent multiple simultaneous fetches
    if (loading) return dashboardData;

    const isColdStart = !dashboardData;
    if (isColdStart) setLoading(true);

    try {
      const [dashStatsRes, reportsStatsRes, grievancesStatsRes, sentimentRes, categoryAnalyticsRes] = await Promise.all([
        // Alerts and Reports are NOT geographically scoped, by design.
        //
        // An Alert is a keyword/risk flag on a piece of content — hate speech,
        // a velocity spike, a policy violation. Place is incidental to it, and
        // only the subset that passes the BSK relevance gate ever becomes a
        // Grievance (which is where geography actually lives). Filtering these
        // counts by constituency would silently drop every alert that never
        // got promoted, i.e. most of them.
        //
        // The Alerts tile therefore reports platform-wide totals and says so.
        api.get('/alerts/dashboard-stats'),
        api.get('/reports/stats'),
        api.get('/grievances/dashboard-stats', { params: SCOPE_PARAMS }),
        api.get('/grievances/sentiment-analytics', { params: SCOPE_PARAMS }).catch(() => ({ data: null })),
        api.get('/grievances/category-analytics', { params: SCOPE_PARAMS }).catch(() => ({ data: null }))
      ]);

      const { byPlatform, pendingByPlatform, viralByPlatform } = dashStatsRes.data;

      // Build alertData in the format the dashboard expects: { active: { all: N, twitter: N, ... }, ... }
      const statuses = ['active', 'acknowledged', 'escalated', 'false_positive'];
      const alertData = {};
      statuses.forEach(status => {
        alertData[status] = {};
        Object.keys(byPlatform).forEach(plat => {
          alertData[status][plat] = byPlatform[plat]?.[status] || 0;
        });
      });

      // Viral counts
      alertData.viral = viralByPlatform;

      // Pending report counts
      const alertPendingReportData = pendingByPlatform;

      // Reports from lightweight backend stats
      const reportData = reportsStatsRes.data?.byPlatform || {};

      // Grievances from lightweight backend stats
      const grievanceData = grievancesStatsRes.data?.byPlatform || {};

      const sentimentAnalytics = sentimentRes?.data || null;

      const newData = {
        alertData,
        alertPendingReportData,
        reportData,
        grievanceData,
        karimnagarSummary: null,
        sentimentAnalytics,
        categoryAnalytics: categoryAnalyticsRes.data || null
      };

      setDashboardData(newData);
      setLastFetchTime(Date.now());
      try {
        localStorage.setItem(CACHE_KEY, JSON.stringify({ data: newData, ts: Date.now() }));
      } catch (error) {
        console.error('Failed to write dashboard cache:', error);
      }
      return newData;
    } catch (error) {
      toast.error('Failed to load dashboard data');
      console.error(error);
      return dashboardData; // Return cached data on error
    } finally {
      if (isColdStart) setLoading(false);
    }
  }, [dashboardData, lastFetchTime, loading]);

  const refreshDashboard = useCallback(() => {
    return fetchDashboardData(true);
  }, [fetchDashboardData]);

  return (
    <DashboardContext.Provider value={{
      dashboardData,
      loading,
      fetchDashboardData,
      refreshDashboard,
      hasCachedData: !!dashboardData
    }}>
      {children}
    </DashboardContext.Provider>
  );
};

export const useDashboard = () => {
  const context = useContext(DashboardContext);
  if (!context) {
    throw new Error('useDashboard must be used within a DashboardProvider');
  }
  return context;
};

export default DashboardContext;
